import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { lockPageScrollOverflowOnly } from '../utils/lockPageScroll';
import { LEARNER_UI_ANIM_MS } from '../utils/learnerUiAnim';
import { useFloatingKeyboardSlideReveal } from '../utils/learnerSlideModalMotion';
import { prefersReducedMotion } from '../utils/subscriptionAccessibility';
import { useHistoryBackClose } from './useHistoryBackClose';

export const LEARNER_SLIDE_MODAL_CLOSE_MS = LEARNER_UI_ANIM_MS;

export interface UseLearnerSlideModalResult {
  mounted: boolean;
  visible: boolean;
  panelRef: React.RefObject<HTMLDivElement | null>;
  requestClose: () => void;
  /** Starts close animation without running `beforeClose` (e.g. after confirm). */
  forceClose: () => void;
  handleExitTransitionEnd: (e: React.TransitionEvent<HTMLDivElement>) => void;
}

export interface UseLearnerSlideModalOptions {
  /** When false, page scroll is not locked (matches quick-settings modal — avoids scrollbar gutter shift). */
  lockScroll?: boolean;
  /** When false, browser Back navigates the page instead of dismissing this modal. Default true. */
  historyBack?: boolean;
  /** Stable history id when `historyBack` is enabled. Auto-generated per hook instance if omitted. */
  historyBackId?: string;
}

/** Mount / reveal / animated dismiss — same cadence as version journey modal. */
export function useLearnerSlideModal(
  open: boolean,
  onClose: () => void,
  beforeClose?: () => boolean,
  options?: UseLearnerSlideModalOptions
): UseLearnerSlideModalResult {
  const lockScroll = options?.lockScroll !== false;
  const historyBack = options?.historyBack !== false;
  const historyIdRef = useRef(
    options?.historyBackId ?? `learner-slide-${Math.random().toString(36).slice(2, 10)}`
  );
  const panelRef = useRef<HTMLDivElement>(null);
  const isClosingRef = useRef(false);
  const finishCloseRef = useRef(false);
  const [mounted, setMounted] = useState(false);
  const [visible, setVisible] = useState(false);

  const finishClose = useCallback(() => {
    if (finishCloseRef.current) return;
    finishCloseRef.current = true;
    isClosingRef.current = false;
    onClose();
    setMounted(false);
  }, [onClose]);

  const startCloseAnimation = useCallback(() => {
    if (finishCloseRef.current || isClosingRef.current) return;
    isClosingRef.current = true;
    setVisible(false);
    // If the panel never became visible (interrupted open), finish on the next frame.
    if (!visible) {
      window.requestAnimationFrame(() => {
        if (!finishCloseRef.current) finishClose();
      });
    }
  }, [visible, finishClose]);

  const requestClose = useCallback(() => {
    if (beforeClose?.()) return;
    startCloseAnimation();
  }, [beforeClose, startCloseAnimation]);

  useLayoutEffect(() => {
    if (open) {
      finishCloseRef.current = false;
      isClosingRef.current = false;
      setMounted(true);
      setVisible(prefersReducedMotion());
      return undefined;
    }
    isClosingRef.current = true;
    setVisible(false);
    return undefined;
  }, [open]);

  const revealPanel = useCallback(() => setVisible(true), []);
  useFloatingKeyboardSlideReveal(open && mounted && !visible && !isClosingRef.current, revealPanel);

  const handleExitTransitionEnd = useCallback(
    (e: React.TransitionEvent<HTMLDivElement>) => {
      if (e.target !== e.currentTarget) return;
      if (e.propertyName !== 'opacity') return;
      if (visible || !isClosingRef.current) return;
      finishClose();
    },
    [visible, finishClose]
  );

  useEffect(() => {
    if (visible || !mounted || !isClosingRef.current) return undefined;
    const timer = window.setTimeout(finishClose, LEARNER_SLIDE_MODAL_CLOSE_MS + 50);
    return () => window.clearTimeout(timer);
  }, [visible, mounted, finishClose]);

  useEffect(() => {
    if (!mounted || !lockScroll) return undefined;
    return lockPageScrollOverflowOnly();
  }, [mounted, lockScroll]);

  useEffect(() => {
    if (!mounted) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mounted, requestClose]);

  const onHistoryBack = useCallback(() => {
    if (beforeClose?.()) return true;
    if (finishCloseRef.current || isClosingRef.current) return true;
    startCloseAnimation();
    return false;
  }, [beforeClose, startCloseAnimation]);

  useHistoryBackClose(historyBack && open, onHistoryBack, historyIdRef.current);

  return {
    mounted,
    visible,
    panelRef,
    requestClose,
    forceClose: startCloseAnimation,
    handleExitTransitionEnd,
  };
}

/** When `open` is omitted, the hook treats mount as open (for conditionally rendered modals). */
export function useLearnerSlideModalOnMount(
  onClose: () => void,
  beforeClose?: () => boolean,
  options?: UseLearnerSlideModalOptions
): UseLearnerSlideModalResult {
  return useLearnerSlideModal(true, onClose, beforeClose, options);
}
